import { compareAsc } from "date-fns"
import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import { Member, Response, Workspace, WorkspaceRole } from "@/types"
import {
  UserRoleSchema,
  WorkspaceSchema,
  transToMember,
  transToWorkspace,
} from "./types"

export interface WorkspaceResponse extends WorkspaceSchema {}

export interface CreateWorkspaceRequest {
  name: string
  users: string[]
}

// for update workspace info
export interface UpdateWorkspaceRequest {
  name: string
  brand_id?: string | null
  contact_email?: string | null
  monthly_end_user_credit_cap?: number | null
  end_user_credit_cap_block_message_i18n?: Partial<
    Record<"en" | "zh-TW" | "ja", string>
  > | null
}

export interface UpdateWorkspaceEndUserIdentityMaskingRequest {
  enabled: boolean
}

export interface UpdateWorkspaceMemberRoleRequest {
  user_id: number
  role_name: string
}

export interface UserRoleResponse extends UserRoleSchema {}

interface UpdateUsers {
  user_ids: number[]
}

export interface UpdateWorkspaceUserRequest extends UpdateUsers {}
export interface AddWorkspaceMembersRequest extends UpdateUsers {}
export interface RemoveWorkspaceMembersRequest extends UpdateUsers {}

export { transToWorkspace as workspaceResponseToWorkspace } from "./types"

export const workspacesResponseToWorkspaces = (
  resp: Response<WorkspaceResponse[]>
): Workspace[] => {
  const newWorkspaces = resp.data?.map(transToWorkspace)
  if (!newWorkspaces) return []

  const sortedWorkspaces = newWorkspaces.sort((a, b) => {
    // sort by locked workspace first (default or not owner)
    const aLocked = a.default || a.myRole !== WorkspaceRole.Owner
    const bLocked = b.default || b.myRole !== WorkspaceRole.Owner
    if (aLocked !== bLocked) return aLocked ? -1 : 1

    // sort by created_at
    return compareAsc(a.createdAt, b.createdAt)
  })
  return sortedWorkspaces
}

export const workspaceUsersResponseToMembers = (
  resp: Response<UserRoleResponse[]>
): Member[] => {
  return resp.data?.map((user) => transToMember(user)) || []
}

export const useGetWorkspacesApi = (organizationId: string) => {
  return useCodeerApi<Response<WorkspaceResponse[]>>(
    `/organizations/${organizationId}/workspaces`,
    FetchMethod.Get
  )
}

export const useGetWorkspaceApi = (organizationId: string, id: string) => {
  return useCodeerApi<Response<WorkspaceResponse>>(
    `/organizations/${organizationId}/workspaces/${id}`,
    FetchMethod.Get
  )
}

export const useGetWorkspaceUsersApi = (organizationId: string, id: string) => {
  return useCodeerApi<Response<UserRoleResponse[]>>(
    `/organizations/${organizationId}/workspaces/${id}/users`,
    FetchMethod.Get
  )
}

export const useCreateWorkspacesApi = (organizationId: string) => {
  return useCodeerApi<Response<WorkspaceResponse>, CreateWorkspaceRequest>(
    `/organizations/${organizationId}/workspaces`,
    FetchMethod.Post
  )
}

export const useUpdateWorkspacesApi = (organizationId: string, id: string) => {
  return useCodeerApi<Response<WorkspaceResponse>, UpdateWorkspaceRequest>(
    `/organizations/${organizationId}/workspaces/${id}`,
    FetchMethod.Put
  )
}

export const useUpdateWorkspaceEndUserIdentityMaskingApi = (
  organizationId: string,
  id: string
) => {
  return useCodeerApi<
    Response<WorkspaceResponse>,
    UpdateWorkspaceEndUserIdentityMaskingRequest
  >(
    `/organizations/${organizationId}/workspaces/${id}/end-user-identity-masking`,
    FetchMethod.Patch
  )
}

export const useDeleteWorkspacesApi = (organizationId: string, id: string) => {
  return useCodeerApi<Response<unknown>>(
    `/organizations/${organizationId}/workspaces/${id}`,
    FetchMethod.Delete
  )
}

export const useAddWorkspaceMembersApi = (
  organizationId: string,
  id: string
) => {
  return useCodeerApi<Response<WorkspaceResponse>, AddWorkspaceMembersRequest>(
    `/organizations/${organizationId}/workspaces/${id}/users/add`,
    FetchMethod.Post
  )
}

export const useRemoveWorkspaceMembersApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<
    Response<WorkspaceResponse>,
    RemoveWorkspaceMembersRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/users/remove`,
    FetchMethod.Post
  )
}

export const useLeaveWorkspaceApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<WorkspaceResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/users/leave`,
    FetchMethod.Post
  )
}

export const useUpdateWorkspaceMemberRoleApi = (
  organizationId: string,
  workspaceId: string,
  userId: number
) => {
  return useCodeerApi<
    Response<UserRoleResponse>,
    UpdateWorkspaceMemberRoleRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/users/${userId}/role`,
    FetchMethod.Put
  )
}
